import type { AdminUserListItem, CreateInviteResponse, CurrentMatch, DeclineFeedbackCategory, FeedbackType, InitialMatchingLaunchReadiness, InviteInfo, ManualMatchPreview, ManualMatchQueueItem, MatchProfile, MatchingEligibilityReason, MatchingLaunchStatus, MatchQualityData, ModerationBan, NotificationSettings, Photo, Preferences, Profile, User, WSChatMessage } from "./types";
import type { PreparedImageUpload } from "./image";
import { readOfflineUser, readStoredAccessToken, readStoredRefreshToken, writeStoredAccessToken, writeStoredRefreshToken } from "./authStorage";

export const API_URL = (import.meta.env.VITE_API_URL || "https://api.beyara.app").replace(/\/$/, "");
export const PRIVATE_CACHE_USER_HEADER = "X-Yara-Cache-User";

interface RequestOptions extends RequestInit {
  requiresAuth?: boolean;
  isRefreshRequest?: boolean;
  timeoutMs?: number;
}

type BlobConstraints = {
  contentType: string;
  minBytes: number;
  maxBytes: number;
};

class ApiError extends Error {
  statusCode?: number;
  data?: unknown;
}

export { ApiError };

function normalizePreferencesPayload(preferences: Partial<Preferences>): Partial<Preferences> {
  const normalized = { ...preferences };
  normalized.preferredLanguages = [...new Map(
    (preferences.preferredLanguages ?? [])
      .map((language) => language.normalize("NFKC").trim().replace(/\s+/g, " "))
      .filter(Boolean)
      .map((language) => [language.toLocaleLowerCase("en-US"), language]),
  ).values()];
  if (normalized.preferredLanguages.length === 0) {
    normalized.languageStrictMatch = false;
  }
  if (!normalized.minEducationLevel) {
    delete normalized.minEducationLevel;
  }
  if (normalized.minHeight == null) {
    delete normalized.minHeight;
  }
  if (normalized.maxHeight == null) {
    delete normalized.maxHeight;
  }
  if (normalized.wantsChildren === undefined) {
    delete normalized.wantsChildren;
  }
  return normalized;
}

export function formatApiError(error: unknown): string {
  if (!(error instanceof ApiError)) {
    return error instanceof Error ? error.message : String(error);
  }

  const data = error.data as {
    error?: string;
    details?: Array<{ path: (string | number)[]; message: string }>;
  } | undefined;
  const detail = data?.details?.[0];
  if (detail) {
    const field = detail.path.join(".");
    return field ? `${field}: ${detail.message}` : detail.message;
  }

  return error.message;
}

let apiAccessToken: string | null = readStoredAccessToken();

export function setApiAccessToken(token: string | null) {
  apiAccessToken = token;
}

export function syncApiAccessTokenFromStorage() {
  apiAccessToken = readStoredAccessToken();
}

function resolveApiAccessToken() {
  if (apiAccessToken) return apiAccessToken;
  const stored = readStoredAccessToken();
  if (stored) apiAccessToken = stored;
  return apiAccessToken;
}

if (import.meta.hot) {
  import.meta.hot.accept(() => {
    syncApiAccessTokenFromStorage();
  });
}

const CONNECTION_EVENT = "yara:connection-state";

function publishConnectionState(detail: { offline: boolean; cached: boolean; endpoint: string }) {
  if (typeof window === "undefined") return;
  window.dispatchEvent(new CustomEvent(CONNECTION_EVENT, { detail }));
}

export function isOfflineApiResponse(response: Response) {
  return response.headers.get("X-Yara-Offline") === "1" || response.headers.get("X-Yara-Cache") === "HIT";
}

export class ApiClient {
  private refreshing: Promise<void> | null = null;

  private async requestResponse(endpoint: string, options: RequestOptions = {}): Promise<Response> {
    const { requiresAuth = true, isRefreshRequest = false, timeoutMs = 15000, headers, ...fetchOptions } = options;
    const baseHeaders = new Headers(headers);
    if (!baseHeaders.has("Content-Type") && !(fetchOptions.body instanceof FormData)) {
      baseHeaders.set("Content-Type", "application/json");
    }

    const url = `${API_URL}${endpoint}`;
    const buildHeaders = () => {
      const finalHeaders = new Headers(baseHeaders);
      const token = resolveApiAccessToken();
      if (requiresAuth && token && !finalHeaders.has("Authorization")) {
        finalHeaders.set("Authorization", `Bearer ${token}`);
      }
      if (requiresAuth && fetchOptions.method === "GET" && !finalHeaders.has(PRIVATE_CACHE_USER_HEADER)) {
        const offlineUser = readOfflineUser();
        if (offlineUser?.id) finalHeaders.set(PRIVATE_CACHE_USER_HEADER, offlineUser.id);
      }
      return finalHeaders;
    };
    const performFetch = () => this.fetchWithTimeout(url, {
      ...fetchOptions,
      credentials: "include",
      headers: buildHeaders(),
    }, timeoutMs);

    let response: Response;
    try {
      response = await performFetch();
    } catch (error) {
      if (typeof navigator !== "undefined" && !navigator.onLine) {
        publishConnectionState({ offline: true, cached: false, endpoint });
      }
      if (error instanceof ApiError) {
        throw error;
      }
      if (error instanceof Error && error.message === "Failed to fetch") {
        throw new ApiError("Could not reach Yara right now. Please try again.");
      }
      throw error;
    }

    if (response.status === 401 && requiresAuth && !isRefreshRequest) {
      await this.refresh();
      response = await performFetch();
    }

    const offlineResponse = isOfflineApiResponse(response);
    publishConnectionState({ offline: offlineResponse, cached: offlineResponse, endpoint });

    if (!response.ok) {
      const data = await response.json().catch(() => ({ error: response.statusText }));
      const error = new ApiError(data.message || data.error || "Request failed");
      error.statusCode = response.status;
      error.data = data;
      throw error;
    }

    return response;
  }

  async request<T>(endpoint: string, options: RequestOptions = {}): Promise<T> {
    const response = await this.requestResponse(endpoint, options);
    if (response.status === 204) {
      return {} as T;
    }

    return response.json() as Promise<T>;
  }

  async blob(
    endpoint: string,
    options: RequestOptions = {},
    constraints?: BlobConstraints,
  ): Promise<Blob> {
    const response = await this.requestResponse(endpoint, options);
    const blob = await response.blob();
    if (constraints) {
      const type = blob.type.split(";", 1)[0]?.trim().toLowerCase();
      if (
        type !== constraints.contentType
        || blob.size < constraints.minBytes
        || blob.size > constraints.maxBytes
      ) {
        throw new ApiError("The photo response was invalid. Please try again.");
      }
    }
    return blob;
  }

  private async fetchWithTimeout(url: string, options: RequestInit, timeoutMs: number): Promise<Response> {
    if (timeoutMs <= 0) {
      return fetch(url, options);
    }

    const controller = new AbortController();
    const callerSignal = options.signal;
    let abortSource: "caller" | "timeout" | null = null;
    const abortFromCaller = () => {
      if (controller.signal.aborted) return;
      abortSource = "caller";
      controller.abort(callerSignal?.reason);
    };
    if (callerSignal?.aborted) {
      abortFromCaller();
    } else {
      callerSignal?.addEventListener("abort", abortFromCaller, { once: true });
    }
    const timeoutId = globalThis.setTimeout(() => {
      if (controller.signal.aborted) return;
      abortSource = "timeout";
      controller.abort();
    }, timeoutMs);

    try {
      return await fetch(url, { ...options, signal: controller.signal });
    } catch (error) {
      if (abortSource === "timeout") {
        throw new ApiError("Request timed out");
      }
      throw error;
    } finally {
      globalThis.clearTimeout(timeoutId);
      callerSignal?.removeEventListener("abort", abortFromCaller);
    }
  }

  private async refresh(): Promise<void> {
    if (!this.refreshing) {
      const refreshToken = readStoredRefreshToken();
      this.refreshing = this.request<{ accessToken?: string; refreshToken?: string }>("/auth/refresh", {
        method: "POST",
        body: JSON.stringify(refreshToken ? { refreshToken } : {}),
        requiresAuth: false,
        isRefreshRequest: true,
      }).then((data) => {
        if (data.accessToken) {
          setApiAccessToken(data.accessToken);
          writeStoredAccessToken(data.accessToken);
        }
        if (data.refreshToken) {
          writeStoredRefreshToken(data.refreshToken);
        }
      }).then(() => undefined).finally(() => {
        this.refreshing = null;
      });
    }
    return this.refreshing;
  }

  get<T>(endpoint: string, options?: RequestOptions) {
    return this.request<T>(endpoint, { ...options, method: "GET" });
  }

  post<T>(endpoint: string, data?: unknown, options?: RequestOptions) {
    return this.request<T>(endpoint, {
      ...options,
      method: "POST",
      body: data instanceof FormData ? data : data === undefined ? undefined : JSON.stringify(data),
    });
  }

  put<T>(endpoint: string, data?: unknown, options?: RequestOptions) {
    return this.request<T>(endpoint, {
      ...options,
      method: "PUT",
      body: data === undefined ? undefined : JSON.stringify(data),
    });
  }

  patch<T>(endpoint: string, data?: unknown, options?: RequestOptions) {
    return this.request<T>(endpoint, {
      ...options,
      method: "PATCH",
      body: data === undefined ? undefined : JSON.stringify(data),
    });
  }

  delete<T>(endpoint: string, data?: unknown, options?: RequestOptions) {
    return this.request<T>(endpoint, {
      ...options,
      method: "DELETE",
      body: data === undefined ? undefined : JSON.stringify(data),
    });
  }
}

export const client = new ApiClient();

export function getWebSocketUrl(
  matchId: string,
  accessToken?: string | null,
  locationLike: Pick<Location, "protocol" | "host"> = window.location
): string {
  const wsBase = API_URL.startsWith("http")
    ? API_URL.replace(/^http/, "ws")
    : `${locationLike.protocol === "https:" ? "wss" : "ws"}://${locationLike.host}`;
  const tokenParam = API_URL.startsWith("http") && accessToken ? `?token=${encodeURIComponent(accessToken)}` : "";
  return `${wsBase}/chat/${encodeURIComponent(matchId)}/ws${tokenParam}`;
}

export const api = {
  login: (email: string, password: string) =>
    client.post<{ user: User; accessToken?: string; refreshToken?: string }>("/auth/login", { email, password }, { requiresAuth: false }),
  signup: (email: string, password: string, inviteCode: string) =>
    client.post<{ user: User; accessToken?: string; refreshToken?: string }>("/auth/signup", { email, password, inviteCode }, { requiresAuth: false }),
  verifyEmail: (token: string) =>
    client.post<{ message: string; isEmailVerified: true }>("/auth/verify-email", { token }, { requiresAuth: false }),
  resendEmailVerification: () =>
    client.post<{ message: string; isEmailVerified: boolean }>("/auth/resend-verification", {}),
  requestPasswordReset: (email: string) =>
    client.post<{ message: string }>("/auth/forgot-password", { email }, { requiresAuth: false }),
  validatePasswordResetToken: (token: string) =>
    client.post<{ valid: true }>("/auth/reset-password/validate", { token }, { requiresAuth: false }),
  resetPassword: (token: string, password: string) =>
    client.post<{ message: string; sessionsInvalidated: true }>("/auth/reset-password", { token, password }, { requiresAuth: false }),
  logout: () => client.post("/auth/logout", {}),
  me: async () => (await client.get<{ user: User }>("/auth/me", { timeoutMs: 5000 })).user,
  getProfileResponse: () => client.get<{ profile: Profile; matchingEligibility: { isEligible: boolean; reasons: string[]; reasonKeys: MatchingEligibilityReason[]; hasActiveMatch: boolean } }>("/profile"),
  createProfile: (profile: Partial<Profile>) => client.post("/profile", profile),
  updateProfile: (profile: Partial<Profile>) => client.put<Profile>("/profile", profile),
  uploadPhoto: ({ uploadId, photo, thumbnail }: PreparedImageUpload) => {
    const body = new FormData();
    body.append("uploadId", uploadId);
    body.append("photo", photo);
    body.append("thumbnail", thumbnail);
    return client.post<{ photo: Photo }>("/profile/photos", body);
  },
  deletePhoto: (photoId: string) =>
    client.delete<{ message: string }>(`/profile/photos/${encodeURIComponent(photoId)}`),
  setProfilePicture: (photoId: string) =>
    client.put<{ message: string }>(`/profile/photos/${encodeURIComponent(photoId)}/profile-picture`),
  getPhotoBlob: (
    photoId: string,
    variant: "full" | "thumbnail",
    signal?: AbortSignal,
  ) =>
    client.blob(`/photos/${encodeURIComponent(photoId)}/${variant}`, {
      method: "GET",
      timeoutMs: 20000,
      signal,
    }, {
      contentType: "image/jpeg",
      minBytes: 30,
      maxBytes: variant === "full" ? 1.5 * 1024 * 1024 : 160 * 1024,
    }),
  getPreferences: async () => (await client.get<{ preferences: Preferences }>("/preferences")).preferences,
  createPreferences: (preferences: Partial<Preferences>) =>
    client.post("/preferences", normalizePreferencesPayload(preferences)),
  updatePreferences: (preferences: Partial<Preferences>) =>
    client.put("/preferences", normalizePreferencesPayload(preferences)),
  getCurrentMatch: async () => (await client.get<{ match: CurrentMatch | null }>("/matches/current")).match,
  getMatchingLaunchStatus: () => client.get<MatchingLaunchStatus>("/matches/launch-status"),
  respondToMatch: (matchId: string, response: "accept" | "reject", declineFeedback?: { category?: DeclineFeedbackCategory; details?: string }) => client.post(`/matches/${matchId}/respond`, { response, declineFeedback }),
  acknowledgeRejection: (matchId: string) => client.post(`/matches/${matchId}/acknowledge-rejection`),
  markMatchSeen: (matchId: string) => client.post(`/matches/${matchId}/mark-seen`, {}),
  disconnectMatch: (reason: string, feedback?: string) => client.post("/matches/current/disconnect", { reason, feedback, shareFeedback: false }),
  reportMatch: (type: string, description: string) => client.post("/matches/current/report", { type, description }),
  getChatMessages: async (matchId: string) => (await client.get<{ messages: WSChatMessage[] }>(`/chat/${matchId}`)).messages,
  getNotificationSettings: async () => (await client.get<{ settings: NotificationSettings }>("/settings/notifications")).settings,
  updateNotificationSettings: (settings: Partial<NotificationSettings>) => client.put("/settings/notifications", settings),
  deactivateAccount: () => client.post<{ message: string }>("/settings/deactivate", {}),
  reactivateAccount: () => client.post<{ message: string }>("/settings/reactivate", {}),
  deleteAccount: () => client.delete<{ message: string }>("/settings/account"),
  getInvites: () => client.get<InviteInfo>("/invites"),
  createInvite: (inviteeEmail?: string) => client.post<CreateInviteResponse>("/invites", inviteeEmail ? { inviteeEmail } : {}),
  resendInvite: (code: string) => client.post<CreateInviteResponse>(`/invites/${encodeURIComponent(code)}/resend`, {}),
  deleteInvite: (code: string) => client.delete<{ message: string; invitesRemaining: number; invitesUnlimited: boolean }>(`/invites/${encodeURIComponent(code)}`),
  submitFeedback: (type: FeedbackType, subject: string, message: string) => client.post("/feedback", { type, subject, message }),
  getAdminUsers: (params: { filter?: string; search?: string }) => {
    const query = new URLSearchParams();
    if (params.filter) query.set("filter", params.filter);
    if (params.search) query.set("search", params.search);
    const suffix = query.toString() ? `?${query.toString()}` : "";
    return client.get<{ users: AdminUserListItem[] }>(`/admin/users${suffix}`);
  },
  getMatchQuality: (window: "7d" | "30d" | "90d" | "all") => client.get<MatchQualityData>(`/admin/match-quality?window=${window}`),
  getInitialMatchingLaunchReadiness: () => client.get<InitialMatchingLaunchReadiness>("/admin/matching-launch"),
  recalculateInitialMatchingLaunch: () => client.post<InitialMatchingLaunchReadiness>("/admin/matching-launch/recalculate", {}),
  startInitialMatchingLaunch: () => client.post<{
    started: boolean;
    alreadyLive: boolean;
    readiness: InitialMatchingLaunchReadiness;
    result: {
      matchesCreated: number;
      usersProcessed: number;
      errors: string[];
      launchPhase: "collecting" | "live";
      launched: boolean;
      launchTrigger: "early" | "standard" | null;
      skippedReason?: "collecting" | "already_live" | "round_locked";
    };
  }>("/admin/matching-launch/start", {}),
  getManualMatchQueue: () => client.get<{ entries: ManualMatchQueueItem[] }>("/admin/matching-queue/manual"),
  previewManualMatch: (userId: string, partnerUserId: string) =>
    client.post<{ preview: ManualMatchPreview }>("/admin/matching-queue/manual/preview", {
      userId,
      partnerUserId,
    }),
  queueManualMatch: (body: {
    userId: string;
    partnerUserId: string;
    adminNote?: string;
  }) => client.post<{ entry: ManualMatchQueueItem }>("/admin/matching-queue/manual", body),
  createManualMatchImmediately: (body: {
    userId: string;
    partnerUserId: string;
    adminNote?: string;
  }) => client.post<{ matchId: string }>("/admin/matching-queue/manual/immediate", body),
  cancelManualMatch: (queueEntryId: string) =>
    client.post<{ message: string }>(
      `/admin/matching-queue/manual/${encodeURIComponent(queueEntryId)}/cancel`,
      {},
    ),
  resolveAdminUser: (email: string) => client.get<{
    user: { id: string; email: string; firstName: string | null };
  }>(`/admin/users/resolve?email=${encodeURIComponent(email)}`),
  getAdminUser: (id: string) => client.get<{
    user: {
      id: string;
      email: string;
      status: string;
      role: "user" | "admin" | "moderator";
      matchingApproved: boolean;
      moderationBan: ModerationBan;
      suspendedUntil: string | null;
      isBanned: boolean;
      inviterId: string | null;
      inviterEmail: string | null;
      photoCount: number;
      createdAt: string;
    };
    profile: MatchProfile | null;
    preferences: Preferences | null;
    reports: Array<{
      id: string;
      reporterId: string;
      reporterEmail: string | null;
      type: string;
      description: string;
      status: string;
      reviewNotes: string | null;
      actionTaken: string | null;
      createdAt: string;
      reviewedAt: string | null;
    }>;
    matches: Array<{
      id: string;
      otherUserId: string;
      otherFirstName: string;
      status: string;
      statusLabel: string;
      canViewChat: boolean;
      reports: Array<{
        id: string;
        type: string;
        description: string;
        status: string;
        reviewNotes: string | null;
        actionTaken: string | null;
        reporterEmail: string | null;
        createdAt: string;
      }>;
      createdAt: string;
      updatedAt: string;
      connectedAt: string | null;
      disconnectedAt: string | null;
      disconnectedBy: string | null;
    }>;
    suspensionHistory: Array<{
      id: string;
      kind: "temporary_ban" | "permanent_ban" | "lifted";
      label: string;
      note: string | null;
      actorId: string | null;
      actorEmail: string | null;
      createdAt: string;
      days: number | null;
    }>;
  }>(`/admin/users/${encodeURIComponent(id)}`),
  getAdminUserMatches: (id: string) => client.get<{
    matches: Array<{
      id: string;
      otherUserId: string;
      otherFirstName: string;
      status: string;
      statusLabel: string;
      canViewChat: boolean;
      reports: Array<{
        id: string;
        type: string;
        description: string;
        status: string;
        reviewNotes: string | null;
        actionTaken: string | null;
        reporterEmail: string | null;
        createdAt: string;
      }>;
      createdAt: string;
      updatedAt: string;
      connectedAt: string | null;
      disconnectedAt: string | null;
      disconnectedBy: string | null;
    }>;
  }>(`/admin/users/${encodeURIComponent(id)}/matches`),
  approveAdminUser: (id: string) => client.post(`/admin/users/${encodeURIComponent(id)}/approve`, {}),
  suspendAdminUser: (
    id: string,
    body: ({ permanent: true } | { days: number }) & { note?: string },
  ) => client.post(`/admin/users/${encodeURIComponent(id)}/suspend`, body),
  unsuspendAdminUser: (id: string) => client.post(`/admin/users/${encodeURIComponent(id)}/unsuspend`, {}),
  reviewAdminReport: (
    reportId: string,
    body: {
      status: "resolved" | "dismissed";
      actionTaken: "none" | "warning" | "temporary_ban" | "permanent_ban";
      reviewNotes?: string;
      banDays?: number;
    },
  ) => client.post(`/admin/reports/${encodeURIComponent(reportId)}/review`, body),
  getAdminMatchChat: (matchId: string, memberId: string) => client.get<{
    messages: Array<{
      id: string;
      senderId: string;
      senderFirstName: string;
      content: string;
      createdAt: string;
      replyToId: string | null;
    }>;
  }>(`/admin/matches/${encodeURIComponent(matchId)}/chat?memberId=${encodeURIComponent(memberId)}`),
  reverseGeocode: (lat: number, lng: number) => client.get<{
    city: string;
    state?: string;
    country: string;
    countryCode?: string;
    placeId?: string;
    countryTrust?: {
      status: "matched" | "mismatched" | "mismatch_allowed" | "network_unavailable" | "resolved_country_unavailable";
      resolvedCountry: string | null;
      networkCountry: string | null;
    };
  }>(`/location/reverse-geocode?lat=${lat}&lng=${lng}`),
};
